import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { StarIcon as StarOutline } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import apiService from '../services/ApiService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatCompactCount } from '../utils/engagementMeta';

interface FavoriteBuildButtonProps {
  vehicleId: number;
  initialFavorited?: boolean;
  initialFavoriteCount?: number;
  size?: 'sm' | 'md' | 'lg';
  showCount?: boolean;
  className?: string;
  onFavoriteChange?: (favorited: boolean, favoriteCount: number) => void;
}

const sizeConfig = {
  sm: { icon: 'w-4 h-4', text: 'text-xs', gap: 'gap-1' },
  md: { icon: 'w-5 h-5', text: 'text-sm', gap: 'gap-1.5' },
  lg: { icon: 'w-6 h-6', text: 'text-base', gap: 'gap-2' },
};

/** Private save to revisit (star). Distinct from Like (heart / public appreciation). */
export const FavoriteBuildButton: React.FC<FavoriteBuildButtonProps> = ({
  vehicleId,
  initialFavorited = false,
  initialFavoriteCount = 0,
  size = 'md',
  showCount = true,
  className = '',
  onFavoriteChange,
}) => {
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [favorited, setFavorited] = useState(initialFavorited);
  const [favoriteCount, setFavoriteCount] = useState(initialFavoriteCount);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setFavorited(initialFavorited);
    setFavoriteCount(initialFavoriteCount);
  }, [initialFavorited, initialFavoriteCount, vehicleId]);

  const cfg = sizeConfig[size];

  const redirectToLogin = () => {
    void apiService.trackEvent('favorite_signin_started', {
      vehicle_id: vehicleId,
      metadata: { from: location.pathname + location.search },
    });
    navigate('/login', { state: { from: location.pathname + location.search } });
  };

  const handleToggle = async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (pending) return;

    if (!isAuthenticated) {
      redirectToLogin();
      return;
    }

    const nextFavorited = !favorited;
    const previousFavorited = favorited;
    const previousCount = favoriteCount;
    const nextCount = Math.max(0, favoriteCount + (nextFavorited ? 1 : -1));

    setFavorited(nextFavorited);
    setFavoriteCount(nextCount);
    setPending(true);
    onFavoriteChange?.(nextFavorited, nextCount);

    try {
      const response = nextFavorited
        ? await apiService.favoriteVehicle(vehicleId)
        : await apiService.unfavoriteVehicle(vehicleId);
      setFavorited(Boolean(response.is_favorited));
      setFavoriteCount(Number(response.favorite_count ?? nextCount));
      onFavoriteChange?.(Boolean(response.is_favorited), Number(response.favorite_count ?? nextCount));
      showToast(nextFavorited ? 'Added to favorites.' : 'Removed from favorites.', 'success');
      void apiService.trackEvent(nextFavorited ? 'vehicle_favorited' : 'vehicle_unfavorited', {
        vehicle_id: vehicleId,
      });
    } catch (err: unknown) {
      setFavorited(previousFavorited);
      setFavoriteCount(previousCount);
      onFavoriteChange?.(previousFavorited, previousCount);
      const message = err instanceof Error ? err.message : '';
      if (message.toLowerCase().includes('authentication')) {
        redirectToLogin();
      } else {
        showToast(message || 'Couldn’t update favorites. Try again.', 'error');
      }
    } finally {
      setPending(false);
    }
  };

  const label = favorited ? 'Remove from favorites' : 'Favorite';

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={pending}
      aria-pressed={favorited}
      aria-label={label}
      title={label}
      className={[
        'relative inline-flex items-center justify-center min-h-[44px] min-w-[44px]',
        'rounded-lg border border-white/[0.10] bg-[#141414]/70 backdrop-blur-sm',
        'text-gray-200 hover:bg-white/[0.06] hover:text-white',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900',
        'transition-colors select-none',
        pending ? 'opacity-70 cursor-wait' : 'cursor-pointer',
        favorited ? 'border-amber-500/40 text-amber-200' : '',
        cfg.gap,
        'px-2.5',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {favorited ? (
        <StarSolid className={`${cfg.icon} text-amber-400`} aria-hidden />
      ) : (
        <StarOutline className={cfg.icon} aria-hidden />
      )}
      {showCount ? (
        <span className={`${cfg.text} font-medium tabular-nums`}>{formatCompactCount(favoriteCount)}</span>
      ) : null}
    </button>
  );
};

export default FavoriteBuildButton;
